import type { CognitoGlobal as CoreCognitoGlobal } from '@legacy/Scripts/required/Cognito-core';
import { ready, setReadyDependencies } from './cognito-ready';
import type { ExoWebContext, ExoWebEntity, ExoWebModel, ExoWebType } from './exoweb';
import type { VueElement } from 'vue';

/**
 * Ensure that the Cognito global is present on the given window object
 */
export function ensureCognitoGlobal(): CoreCognitoGlobal {
	const win = window as any;
	let Cognito = win.Cognito as CoreCognitoGlobal;
	if (!Cognito) {
		const readyDependencies = {};
		setReadyDependencies(readyDependencies);
		const templateComponents = [];
		Cognito = win.Cognito = { ready, readyDependencies, templateComponents };
	}
	else {
		// Establish readyDependencies if needed and assign to module storage
		const readyDependencies = Cognito.readyDependencies || {};
		Cognito.readyDependencies = readyDependencies;
		setReadyDependencies(readyDependencies);
		// Establish templateComponents array if needed
		Cognito.templateComponents = Cognito.templateComponents || [];
	}
	Cognito.ready = ready;
	return Cognito;
}

/**
 * Cleans up event handlers
 */
function removeGlobalJQueryEventHandlers() {
	const $ = (window as any).$;
	$(document.body).off();
	$(document).off();
	$(document.documentElement).off();
	$(window).off();
}

const modelPropertyNativeTypes = [String, Date, Number, Boolean];

/**
 * Gets rid of global state created by legacy page scripts, ex: MS Ajax, ExoWeb, window.Cognito, etc.
 */
export function disposeLegacyPageState(pageElement: HTMLElement) {
	const Cognito = ensureCognitoGlobal();
	const win = window as any;

	removeGlobalJQueryEventHandlers();
	win.VueComponents?.closeAllDialogs();
	win.$.fn.dialog.constructor.activeDialogs.forEach(dlg => dlg.close(true));
	// explicitly clean up dialogs since they get moved out of the msajax element
	document.body.querySelectorAll('.c-modal').forEach((dlg: VueElement) => !dlg.__vue__ && !dlg.parentElement.__vue__ && dlg.remove());

	win.Sys.Application.disposeElement(document.documentElement);
	// Reset any ms-ajax-template components so that they will re-initialize if needed
	Cognito.templateComponents.forEach(vm => {
		vm.reset();
	});
	if (win.Cognito?.timers) {
		for (const id of Object.values<string>(win.Cognito.timers)) {
			clearTimeout(id);
			clearInterval(id);
		}
	}
	// should abort all requests
	if (win.Cognito?.abortRequests) {
		win.Cognito.abortRequests();
	}

	// Removed cached ExoWeb formats on native types
	modelPropertyNativeTypes.forEach(t => delete (t as any)._formats);

	win.Cognito?.Forms?.db?.close();
	win.Cognito?.Messaging?.dispose();
	delete win.Cognito;
	delete win.context?.server;
	delete win.context?.model;
	delete win.context;
	for (const k of Object.keys(win.ExoWeb.Model.ConditionType.allConditionTypes))
		delete win.ExoWeb.Model.ConditionType.allConditionTypes[k];
	for (const k of Object.keys(win.ExoWeb.Model.ConditionTypeSet.allConditionTypeSets))
		delete win.ExoWeb.Model.ConditionTypeSet.allConditionTypeSets[k];
	for (const k of Object.keys(win.ExoWeb.Model.Model.types))
		delete win.ExoWeb.Model.Model.types[k];
	// unregister msajax "templates"
	for (const componentId of Object.keys(win.Sys.Application._components))
		win.Sys.Application.removeComponent(win.Sys.Application._components[componentId]);
}

type ExoWebModelPrivateMembers = {
	_types: { [typeName: string]: ExoWebType };
};

type ExoWebTypePrivateMembers = {
	_pool: { [id: string]: ExoWebEntity };
	_legacyPool: { [id: string]: ExoWebEntity };
};

/**
 * Resets the entity pool for all known types.
 */
export function resetEntityPools() {
	if ((window as any).context) {
		const context = (window as any).context as ExoWebContext;
		const model = context.model.meta as ExoWebModel & ExoWebModelPrivateMembers;
		Object.keys(model._types).forEach(function (typeName) {
			const type = model._types[typeName] as ExoWebType & ExoWebTypePrivateMembers;
			type._pool = {};
			type._legacyPool = {};
			type.get_properties().filter(p => p.get_isStatic()).forEach(p => {
				if (p.get_isEntityType()) {
					// Null out entity reference
					p.value(null, null);
				}
				else if (p.get_isEntityListType()) {
					// Clear out entity list
					const list = p.value<ExoWebEntity[]>(null);
					list.length = 0;
				}
			});
		});
	}
};
